import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiClient } from '../../utils/apiClient';
import { useDossierResource } from './hooks/useDossierResource';
import { DossierProvider } from './DossierContext';
import DossierLoadingView from './DossierLoadingView';
import DossierUnauthorizedView from './DossierUnauthorizedView';
import InvitationPrompt from '../components/InvitationPrompt';
import AccessRequestPrompt from '../components/AccessRequestPrompt';

/**
 * Gate qui transforme l'union discriminée `DossierResource` en JSX :
 *   - loading        → spinner plein écran
 *   - unauthorized   → écran "pas d'accès" + retour à /dossiers
 *   - invitation     → `<InvitationPrompt>` (accept / reject / admin bypass)
 *   - access-required→ `<AccessRequestPrompt>` (demande d'accès)
 *   - ready          → wrap les enfants dans `<DossierProvider>`
 *
 * Les enfants ne voient JAMAIS un dossier null : la garantie de
 * type est assurée par ce composant.
 */
interface DossierResourceGateProps {
  children: React.ReactNode;
}

const DossierResourceGate: React.FC<DossierResourceGateProps> = ({ children }) => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const { resource, refetch } = useDossierResource({
    dossierId: id,
    isAuthenticated,
  });

  const handleAcceptInvitation = useCallback(async () => {
    if (resource.status !== 'invitation') return;
    try {
      const response = await apiClient(
        `/invitations/${resource.invitation.token}/respond`,
        {
          method: 'POST',
          body: JSON.stringify({ status: 'accepted' }),
        }
      );
      if (response.ok) {
        await refetch();
      } else {
        console.error("Erreur lors de l'acceptation de l'invitation", response.status);
      }
    } catch (error) {
      console.error("Erreur réseau lors de l'acceptation de l'invitation", error);
    }
  }, [resource, refetch]);

  const handleRejectInvitation = useCallback(async () => {
    if (resource.status !== 'invitation') return;
    try {
      const response = await apiClient(
        `/invitations/${resource.invitation.token}/respond`,
        {
          method: 'POST',
          body: JSON.stringify({ status: 'rejected' }),
        }
      );
      if (response.ok) {
        navigate('/dossiers');
      } else {
        console.error("Erreur lors du refus de l'invitation", response.status);
      }
    } catch (error) {
      console.error("Erreur réseau lors du refus de l'invitation", error);
    }
  }, [resource, navigate]);

  const handleAdminBypass = useCallback(() => {
    if (resource.status !== 'invitation') return;
    // L'admin accède directement, sans accepter/refuser l'invitation.
    // L'invitation reste pending côté backend, mais l'admin peut quand
    // même éditer. Le refetch va passer en `ready` puisque le rôle
    // admin est résolu côté backend.
    void refetch();
  }, [resource, refetch]);

  if (resource.status === 'loading') {
    return <DossierLoadingView />;
  }

  if (resource.status === 'unauthorized') {
    return <DossierUnauthorizedView />;
  }

  if (resource.status === 'invitation') {
    return (
      <InvitationPrompt
        dossierTitle={resource.dossierTitle}
        dossierId={id || ''}
        onAccept={handleAcceptInvitation}
        onReject={handleRejectInvitation}
        allowBypass={resource.adminBypass}
        onBypass={handleAdminBypass}
      />
    );
  }

  if (resource.status === 'access-required') {
    return (
      <AccessRequestPrompt
        dossierId={id || ''}
        dossierTitle={resource.dossier?.title || ''}
        dossierDescription={resource.dossier?.description || ''}
      />
    );
  }

  // status === 'ready'
  return (
    <DossierProvider
      dossierId={resource.dossier._id}
      initialDossier={resource.dossier}
      initialLastSaved={resource.lastSaved}
      userRole={resource.userRole}
    >
      {children}
    </DossierProvider>
  );
};

export default DossierResourceGate;
